import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';
import { useMessageContext } from '@post-office/message-context';
import { useMessageInstanceEvent } from '@atlassian/post-office-context/message-lifecycle';

import { Body } from './Body';
import type { AdditionalProps, HydrationResult, TargetProduct } from '../types';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useRequestAccess } from '../useRequestAccess';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

export type Props = HydrationResult &
	AdditionalProps & {
		targetProduct: TargetProduct;
	};

export const JiraSideNavCrossJoinMessage = ({
	targetProduct,
	collaborators,
	onDismissed,
	tenantId,
	userId,
	sourceProduct,
	crossJoinUrl,
}: Props): ReactNode => {
	const isFirstRender = useRef<boolean>(true);
	const [isVisible, setIsVisible] = useState(true);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceViewed } = useMessageInstanceEvent();

	const [isRequestSentFlagVisible, setIsRequestSentFlagVisible] = useState(false);
	const { messageTemplateId } = useMessageContext();
	const requestAccess = useRequestAccess({
		targetProduct,
		tenantId,
		userId,
		sourceProduct,
		collaborators,
		crossJoinUrl,
		messageTemplateId,
		onComplete: () => setIsVisible(false),
		showRequestSentFlag: () => setIsRequestSentFlagVisible(true),
	});

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			void messageInstanceViewed();
			void fireAnalyticsEvent({
				name: 'cjnNav4 placement',
				eventType: 'screen',
				attributes: {
					messageTemplateId,
				},
			});
		}
	}, []);

	const attributes = useMemo(
		() => ({
			...(messageTemplateId ? { messageTemplateId } : {}),
		}),
		[messageTemplateId],
	);

	if (!isVisible) {
		return null;
	}

	return (
		<JiraSideNavMessage
			onDismissed={onDismissed}
			additionalAnalyticsAttributes={attributes}
			bodyContent={
				<Body
					sourceProduct={sourceProduct}
					targetProduct={targetProduct}
					collaborators={collaborators}
					tenantId={tenantId}
					userId={userId}
					crossJoinUrl={crossJoinUrl}
					messageTemplateId={messageTemplateId}
					onRequestAccess={requestAccess}
					isRequestSentFlagVisible={isRequestSentFlagVisible}
					setIsRequestSentFlagVisible={setIsRequestSentFlagVisible}
				/>
			}
		/>
	);
};
